<div class="app-dialog dialog-container" [class.inline-mode]="isInlineMode">
  <!-- Header (hidden in inline mode - parent handles title) -->
  <div class="app-dialog__heading dialog-header" *ngIf="!isInlineMode">
    <button mat-icon-button type="button" class="dialog-close" aria-label="Close" (click)="onCancel()">
      <mat-icon>close</mat-icon>
    </button>
    <div class="app-dialog__heading-copy">
      <span class="app-dialog__eyebrow">Data source</span>
      <div class="app-dialog__title-row">
        <button
          *ngIf="currentStage !== 'connection-type' && currentStage !== 'api-listening'"
          mat-icon-button
          type="button"
          class="app-dialog__back-button"
          aria-label="Back"
          (click)="goBack()">
          <mat-icon svgIcon="arrow-left"></mat-icon>
        </button>
        <div class="app-dialog__title-copy">
          <h2 mat-dialog-title class="app-dialog__title">{{ getDialogTitle() }}</h2>
          <p>Connect a file or API listener to make data available in DQP.</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Back button for inline mode -->
  <div *ngIf="isInlineMode && currentStage !== 'connection-type' && currentStage !== 'api-listening'" class="inline-back-button">
    <button mat-icon-button class="back-button" (click)="goBack()">
      <mat-icon svgIcon="arrow-left"></mat-icon>
    </button>
    <span class="stage-title">{{ getDialogTitle() }}</span>
  </div>

  <!-- Content -->
  <mat-dialog-content class="app-dialog__content dialog-content" [class.inline-content]="isInlineMode">
    
    <!-- Stage 1: Connection Type Selection -->
    <div *ngIf="currentStage === 'connection-type'" class="connection-type-stage">
      <div class="categories-row" [class.full-width-sections]="effectiveData.showExistingDataSources === false">
        <div class="section box-light-grey category-section">
          <h3>Manual Upload</h3>
          <p class="category-description">Upload data files directly from your computer.</p>
          <div class="connection-grid">
            <div *ngFor="let type of getConnectionTypesByCategory('File')" 
                 class="card-selectable connection-card" 
                 [class.selected]="formData.connectionType === type.id"
                 (click)="onConnectionTypeSelect(type)">
              <mat-icon [svgIcon]="type.icon"></mat-icon>
              <div class="connection-info">
                <div class="header-section">
                  <h4>{{ type.label }}</h4>
                  <p class="option-description" *ngIf="type.description">{{ type.description }}</p>
                </div>
                <div class="details-row">
                  <div class="detail-item" *ngIf="type.idealFor">
                    <p class="ideal-for"><strong>Ideal for</strong>{{ type.idealFor }}</p>
                  </div>
                  <div class="detail-item" *ngIf="type.features && type.features.length > 0">
                    <p class="features-info"><strong>Supports</strong>{{ type.features.join(', ') }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="section box-light-grey category-section">
          <h3>Web API</h3>
          <p class="category-description">We'll guide you through the setup process.</p>
          <div class="connection-grid">
            <div *ngFor="let type of getConnectionTypesByCategory('API')" 
                 class="card-selectable connection-card" 
                 [class.selected]="formData.connectionType === type.id"
                 [class.disabled]="isApiListenerDisabled"
                 (click)="!isApiListenerDisabled && onConnectionTypeSelect(type)">
              <mat-icon [svgIcon]="type.icon"></mat-icon>
              <div class="connection-info">
                <div class="header-section">
                  <h4>{{ type.label }}</h4>
                  <p class="option-description" *ngIf="type.description && !isApiListenerDisabled">{{ type.description }}</p>
                  <p class="option-description disabled-message" *ngIf="isApiListenerDisabled">{{ apiListenerDisabledReason }}</p>
                </div>
                <ng-container *ngIf="!isApiListenerDisabled">
                  <div class="details-row">
                    <div class="detail-item" *ngIf="type.idealFor">
                      <p class="ideal-for"><strong>Ideal for</strong>{{ type.idealFor }}</p>
                    </div>
                    <div class="detail-item" *ngIf="type.features && type.features.length > 0">
                      <p class="features-info"><strong>Supports</strong>{{ type.features.join(', ') }}</p>
                    </div>
                  </div>
                </ng-container>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="section box-light-grey" *ngIf="effectiveData.showExistingDataSources !== false">
        <div class="section-header-with-search">
          <h3>Existing Data Sources</h3>
          <mat-form-field class="search-field" appearance="outline">
            <mat-icon matPrefix svgIcon="search"></mat-icon>
            <input matInput 
                   placeholder="Search data sources..." 
                   [(ngModel)]="searchQuery"
                   (ngModelChange)="filterDataSources()">
          </mat-form-field>
        </div>
        <div *ngIf="loadingDataSources" class="loading-container">
          <mat-spinner diameter="30"></mat-spinner>
          <p>Loading data sources...</p>
        </div>
        <div *ngIf="!loadingDataSources && existingDataSources.length === 0" class="empty-state">
          <p>No existing data sources found</p>
        </div>
        <div *ngIf="!loadingDataSources && existingDataSources.length > 0" class="existing-sources-table-container">
          <table mat-table [dataSource]="filteredDataSources" class="data-sources-table">
            <ng-container matColumnDef="name">
              <th mat-header-cell *matHeaderCellDef>Name</th>
              <td mat-cell *matCellDef="let dataSource">{{ dataSource.dataSourceName }}</td>
            </ng-container>

            <ng-container matColumnDef="tags">
              <th mat-header-cell *matHeaderCellDef>Tags</th>
              <td mat-cell *matCellDef="let dataSource" class="tags-cell">
                <ng-container *ngIf="formatTags(dataSource.tags).length > 0; else noTags">
                  <span *ngFor="let tag of formatTags(dataSource.tags)" class="tag-text">
                    {{ tag }}
                  </span>
                </ng-container>
                <ng-template #noTags>
                  <span class="no-tags">-</span>
                </ng-template>
              </td>
            </ng-container>

            <ng-container matColumnDef="createdAt">
              <th mat-header-cell *matHeaderCellDef>Created</th>
              <td mat-cell *matCellDef="let dataSource">{{ dataSource.createdAt | date:'yyyy-MM-dd HH:mm' }}</td>
            </ng-container>

            <ng-container matColumnDef="select">
              <th mat-header-cell *matHeaderCellDef>Action</th>
              <td mat-cell *matCellDef="let dataSource">
                <button mat-icon-button color="primary" (click)="selectExistingDataSource(dataSource)" matTooltip="Select data source" aria-label="Select data source">
                  <mat-icon svgIcon="check_circle"></mat-icon>
                </button>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="['name', 'tags', 'createdAt', 'select']"></tr>
            <tr mat-row *matRowDef="let row; columns: ['name', 'tags', 'createdAt', 'select'];"></tr>
          </table>
        </div>
      </div>
    </div>

    <!-- Stage 2: File Upload -->
    <div *ngIf="currentStage === 'file-upload'" class="file-upload-stage">
      <div class="upload-section box-light-grey">
        <label>Select File</label>
        <div class="file-input-container">
          <input type="text" 
                 class="file-input" 
                 [value]="selectedFile?.name || ''" 
                 readonly 
                 placeholder="No file selected">
          <button mat-raised-button color="primary" (click)="browseFiles()" [disabled]="isUploadingFile">Browse</button>
        </div>
        <div class="field-hint">Supported formats: Excel (.xlsx, .xls), CSV (.csv), JSON (.json)</div>
      </div>

      <div class="form-section box-light-grey">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Data Source Name</mat-label>
          <input matInput 
                 [(ngModel)]="formData.name" 
                 [disabled]="isUploadingFile" 
                 required
                 placeholder="Enter a name for this data source">
        </mat-form-field>
        <div class="field-description">A descriptive name to help you identify this data source later</div>
      </div>

      <div class="form-section box-light-grey">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Tags</mat-label>
          <input matInput 
                 [(ngModel)]="formData.tags" 
                 [disabled]="isUploadingFile"
                 placeholder="Optional: Add comma-separated tags">
        </mat-form-field>
        <div class="field-description">Add tags to organize and filter your data sources (e.g., sales, 2024, quarterly-report)</div>
      </div>
      
      <!-- Upload Progress -->
      <div *ngIf="isUploadingFile" class="upload-progress-section">
        <div class="progress-bar-wrapper">
          <div class="progress-label">
            <span class="progress-text">{{ uploadStatusText }}</span>
            <span class="progress-size" *ngIf="uploadProgress > 0 && uploadProgress < 100 && totalBytes > 0">
              {{ getFileSizeString(uploadedBytes) }} of {{ getFileSizeString(totalBytes) }}
            </span>
            <span class="progress-size" *ngIf="uploadProgress === 0 && totalBytes > 0">
              Preparing {{ getFileSizeString(totalBytes) }}...
            </span>
          </div>
          <mat-progress-bar 
            [mode]="uploadProgress === 0 || uploadProgress === 100 ? 'indeterminate' : 'determinate'" 
            [value]="uploadProgress">
          </mat-progress-bar>
        </div>
      </div>

      <!-- Error state -->
      <div *ngIf="fileUploadError" class="error-section">
        <mat-icon color="warn">error</mat-icon>
        <span>{{ fileUploadError }}</span>
      </div>
    </div>

    <!-- Stage 3: API Setup -->
    <div *ngIf="currentStage === 'api-setup'" class="api-setup-stage">
      <div class="api-setup-panel box-light-grey">
        <div class="field-wrapper">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Name</mat-label>
            <input matInput [(ngModel)]="formData.name" [disabled]="isCreatingApiListener">
          </mat-form-field>
          <div class="field-description">A descriptive name to help you identify this data source later</div>
        </div>

        <div class="field-wrapper">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Tags (optional)</mat-label>
            <input matInput [(ngModel)]="formData.tags" [disabled]="isCreatingApiListener">
          </mat-form-field>
          <div class="field-description">Add tags to organize and filter your data sources (e.g., sales, 2024, quarterly-report)</div>
        </div>

        <div class="field-wrapper">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Description (optional)</mat-label>
            <textarea matInput [(ngModel)]="formData.description" [disabled]="isCreatingApiListener" rows="3"></textarea>
          </mat-form-field>
          <div class="field-description">Additional details about this data source</div>
        </div>
      </div>

      <!-- Loading state -->
      <div *ngIf="isCreatingApiListener" class="loading-section">
        <mat-icon class="spin" svgIcon="refresh"></mat-icon>
        <span>Creating API listener...</span>
      </div>

      <!-- Error state -->
      <div *ngIf="apiListenerError" class="error-section">
        <mat-icon color="warn">error</mat-icon>
        <span>{{ apiListenerError }}</span>
      </div>
    </div>

    <!-- Stage 4: API Listening -->
    <div *ngIf="currentStage === 'api-listening'" class="api-listening-stage">
      <div class="api-info-section box-light-grey" *ngIf="isApiListenerFullySetup()">
        <h4>API Listener Information</h4>
        
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Name:</span>
            <span class="info-value">{{ formData.name }}</span>
          </div>
          <div class="info-item" *ngIf="formData.tags">
            <span class="info-label">Tags:</span>
            <span class="info-value">{{ formData.tags }}</span>
          </div>
          <div class="info-item" *ngIf="formData.description">
            <span class="info-label">Description:</span>
            <span class="info-value">{{ formData.description }}</span>
          </div>
        </div>
      </div>

      <div class="schema-section box-light-grey" *ngIf="isApiListenerFullySetup()">
        <h4>Schema Information</h4>
        
        <div *ngIf="loadingSchema" class="loading-state">
          <mat-spinner diameter="30"></mat-spinner>
          <p>Loading schema...</p>
        </div>
        
        <div *ngIf="!loadingSchema && schemaColumns.length === 0" class="no-schema-message">
          <mat-icon svgIcon="info-circle"></mat-icon>
          <p>No schema data available yet. Schema will be populated when data is first received.</p>
        </div>
        
        <div *ngIf="!loadingSchema && schemaColumns.length > 0" class="schema-table-container">
          <table class="schema-table">
            <thead>
              <tr>
                <th>Column Name</th>
                <th>Data Type</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let column of schemaColumns">
                <td>{{ column.ColumnName }}</td>
                <td>{{ column.DataType }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div class="waiting-message box-light-grey" *ngIf="!isApiListenerFullySetup()">
        <mat-spinner diameter="40"></mat-spinner>
        <div class="waiting-text">
          <p>API Listener is waiting for data</p>
          <p class="waiting-subtitle">Send a sample of your data to the endpoint below so we can identify its structure</p>
        </div>
      </div>
      
      <!-- Instructions Section -->
      <div class="instructions-section" *ngIf="!isApiListenerFullySetup()">
        <h4>
          <mat-icon svgIcon="info-circle"></mat-icon>
          <span>What to do next</span>
        </h4>
        <ol class="instructions-list">
          <li>Copy the <strong>Endpoint URL</strong> and <strong>Token</strong> from below</li>
          <li>Send a sample of your data to the endpoint using the token for authentication</li>
          <li>We'll analyze the structure and create a schema - <strong>your data is not stored</strong></li>
          <li>Once the schema is identified, you can use this API Listener in your projects</li>
        </ol>
        <div class="instructions-note">
          <mat-icon svgIcon="ai-brain-idea"></mat-icon>
          <p>For developers: Use the generated cURL command as a starting point, or integrate the endpoint into your application using any HTTP client.</p>
        </div>
      </div>
      
      <!-- API Configuration Section (visible when waiting for data) -->
      <div class="api-config-section box-light-grey" *ngIf="!isApiListenerFullySetup()">
        <div class="config-item">
          <label>Endpoint</label>
          <div class="input-with-copy">
            <input type="text" 
                   [value]="formData.endpoint" 
                   readonly 
                   (click)="$any($event.target).select()">
            <button mat-raised-button 
                    color="primary" 
                    (click)="copyToClipboard(formData.endpoint!)">
              Copy
            </button>
          </div>
        </div>
        
        <div class="config-item">
          <label>Token</label>
          <div class="input-with-copy">
            <input type="text" 
                   [value]="formData.token || 'No token available'" 
                   readonly 
                   [class.no-token]="!formData.token"
                   (click)="formData.token && $any($event.target).select()">
            <button mat-raised-button 
                    color="primary" 
                    [disabled]="isRegeneratingToken"
                    (click)="formData.token ? copyToClipboard(formData.token!) : regenerateToken()">
              {{ getTokenButtonText() }}
            </button>
          </div>
          <div *ngIf="tokenRegenerationError" class="error-message">
            {{ tokenRegenerationError }}
          </div>
        </div>
        
        <div class="config-item">
          <label>Code Examples</label>
          <mat-tab-group class="code-samples-tabs">
            <mat-tab>
              <ng-template mat-tab-label>
                <div class="tab-label-content">
                  <mat-icon svgIcon="curl"></mat-icon>
                  <span>cURL</span>
                </div>
              </ng-template>
              <div class="code-container">
                <pre><code [highlight]="getCurlCommand()" [language]="'bash'" (click)="selectCurlText($event)"></code></pre>
                <button mat-icon-button 
                        class="copy-code-button"
                        (click)="copyToClipboard(getCurlCommand(), true)"
                        [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                  <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                </button>
              </div>
            </mat-tab>
            <mat-tab>
              <ng-template mat-tab-label>
                <div class="tab-label-content">
                  <mat-icon svgIcon="csharp"></mat-icon>
                  <span>C#</span>
                </div>
              </ng-template>
              <div class="code-container">
                <pre><code [highlight]="getCSharpCode()" [language]="'csharp'" (click)="selectCurlText($event)"></code></pre>
                <button mat-icon-button 
                        class="copy-code-button"
                        (click)="copyToClipboard(getCSharpCode(), true)"
                        [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                  <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                </button>
              </div>
            </mat-tab>
            <mat-tab>
              <ng-template mat-tab-label>
                <div class="tab-label-content">
                  <mat-icon svgIcon="vbnet"></mat-icon>
                  <span>VB.NET</span>
                </div>
              </ng-template>
              <div class="code-container">
                <pre><code [highlight]="getVBNetCode()" [language]="'vbnet'" (click)="selectCurlText($event)"></code></pre>
                <button mat-icon-button 
                        class="copy-code-button"
                        (click)="copyToClipboard(getVBNetCode(), true)"
                        [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                  <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                </button>
              </div>
            </mat-tab>
            <mat-tab>
              <ng-template mat-tab-label>
                <div class="tab-label-content">
                  <mat-icon svgIcon="python"></mat-icon>
                  <span>Python</span>
                </div>
              </ng-template>
              <div class="code-container">
                <pre><code [highlight]="getPythonCode()" [language]="'python'" (click)="selectCurlText($event)"></code></pre>
                <button mat-icon-button 
                        class="copy-code-button"
                        (click)="copyToClipboard(getPythonCode(), true)"
                        [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                  <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                </button>
              </div>
            </mat-tab>
            <mat-tab>
              <ng-template mat-tab-label>
                <div class="tab-label-content">
                  <mat-icon>code</mat-icon>
                  <span>Java</span>
                </div>
              </ng-template>
              <div class="code-container">
                <pre><code [highlight]="getJavaCode()" [language]="'java'" (click)="selectCurlText($event)"></code></pre>
                <button mat-icon-button 
                        class="copy-code-button"
                        (click)="copyToClipboard(getJavaCode(), true)"
                        [title]="codeJustCopied ? 'Copied!' : 'Copy code'">
                  <mat-icon [class.copied]="codeJustCopied">{{ codeJustCopied ? 'check' : 'content_copy' }}</mat-icon>
                </button>
              </div>
            </mat-tab>
          </mat-tab-group>
        </div>
      </div>
    </div>
  </mat-dialog-content>

  <!-- Actions -->
  <mat-dialog-actions class="app-dialog__actions dialog-actions" [class.inline-actions]="isInlineMode" align="end" *ngIf="currentStage !== 'connection-type' && currentStage !== 'api-listening'">
    <button mat-button (click)="onCancel()" [disabled]="isCreatingApiListener || isUploadingFile">Cancel</button>
    <button mat-raised-button 
            color="primary" 
            [disabled]="!canProceed() || isCreatingApiListener || isUploadingFile"
            (click)="onSubmit()">
      {{ getSubmitButtonText() }}
    </button>
  </mat-dialog-actions>

  <!-- Actions for API Listening stage -->
  <mat-dialog-actions class="app-dialog__actions dialog-actions" [class.inline-actions]="isInlineMode" align="end" *ngIf="currentStage === 'api-listening' && isApiListenerFullySetup()">
    <button mat-raised-button 
            color="primary" 
            (click)="onFinishApiListenerSetup()">
      Finish
    </button>
  </mat-dialog-actions>
</div>
